<template>
  <va-form
    :id="isEdit ? pageCategory.id : ''"
    class="page-category__form"
    ref="form"
    :item="pageCategory"
    v-model="form"
    :before-save="beforeSave"
    :after-save="afterSave"
    :custom-redirect-route="
      !!this.$route.query.domainId
        ? {
            name: 'domains_show',
            params: { id: domainId },
            query: { tab: 'page_categories' },
          }
        : {
            name: 'page_categories_list',
          }
    "
    :required-fields-set="requiredFieldsSet">
    <!-- Details -->
    <v-card class="spacing-bottom--16">
      <v-card-title>{{ $t("resources.categories.categories_details_block_title") }}</v-card-title>
      <v-card-subtitle class="spacing-bottom--16">{{
        $t("resources.categories.categories_details_block_subtitle")
      }}</v-card-subtitle>
      <va-text-input
        v-model="form.name"
        class="spacing-bottom--16"
        source="name"
        required
        :label="$t('resources.categories.category_name')"></va-text-input>

      <va-autocomplete-input
        :disabled="isDomainProvided"
        class="spacing-bottom--16"
        required
        source="domain"
        v-model="form.domain"
        :label="$t('resources.categories.category_domain')"
        :choices="domainsList"
        :min-chars="1"
        cache-items
        @change="domainChanged"></va-autocomplete-input>

      <va-slug-checker
        class="spacing-bottom--16"
        v-model="form.slug"
        :domain="form.domain"
        :domain-url="selectedDomainInfo.url + '/category'"
        :label="$t('resources.categories.category_slug')"
        :edit-form="isEdit"
        :source="'slug'"
        :disabled="isEdit"
        :type-to-check="'pageCategorySlug'"
        @updateLoading="val => (this.isLoading = val)"></va-slug-checker>

      <VaImageDragAndDropInput
        :item-id="pageCategory ? pageCategory.icon : ''"
        :label="$t('resources.categories.category_icon')"
        @update:photo="updatePageCategoryIcon"></VaImageDragAndDropInput>
      <p class="page-category__image-disclaimer spacing-bottom--16">
        {{ $t("resources.categories.category_icon_disclaimer") }}
      </p>

      <va-text-input
        class="spacing-bottom--16"
        :rows="3"
        v-model="form.description"
        :label="$t('resources.categories.category_description')"
        :placeholder="$t('resources.categories.category_description_placeholder')"
        multiline></va-text-input>
    </v-card>
    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  // Components
  import VaImageDragAndDropInput from "../custom/VaImageDragAndDropInput";
  import VaSlugChecker from "@/components/custom/VaSlugChecker";
  import RequiredFieldText from "@/components/custom/RequiredFieldText";

  // Mixins
  import crudFormMixin from "@/mixins/crudFormMixin";
  import providedDomain from "@/mixins/providedDomain";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";

  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "PageCategoryForm",
    components: {
      VaImageDragAndDropInput,
      VaSlugChecker,
      RequiredFieldText,
    },
    mixins: [crudFormMixin, providedDomain, ConnectedDomainsList],
    props: {
      pageCategory: {
        type: Object,
        default: () => {},
      },
      // Determines if form is used while editing
      isEdit: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        requiredFormFields: ["name", "domain", "slug"],
        form: {},
        selectedDomainInfo: {},
      };
    },
    computed: {
      domainId() {
        return this.form.domain?.replace("/api/domains/", "");
      },
    },
    async beforeMount() {
      // Transform some of the form values. (Comes back as object from back-end, but has to be transformed to IRI)
      this.form = this.pageCategory ? this.pageCategory : {};
      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
      }
      this.form.domain = this.pageCategory?.domain?.["@id"];
      this.form.icon = this.pageCategory?.icon?.["@id"];

      if (this.form.domain)
        this.selectedDomainInfo = await this.getDomainInfo(this.form.domain.replace("/api/domains/", ""));
    },
    methods: {
      async domainChanged(val) {
        this.selectedDomainInfo = await this.getDomainInfo(val.replace("/api/domains/", ""));
      },
      async getDomainInfo(domainId) {
        try {
          const { data } = await this.$admin.dataProvider.getOne("domains", {
            id: domainId,
          });

          return data;
        } catch (err) {
          this.$admin.toast.error(formErrorMessage(err, this.$t("error")));
        }
      },
      updatePageCategoryIcon(newValue) {
        if (newValue === "") {
          this.form.icon = null;
        } else {
          this.form.icon = newValue["@id"];
        }
      },
      submit() {
        this.$refs.form.onSubmit();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .page-category__image-disclaimer {
    margin-top: 8px;
    @include text-caption();
    color: get-color("subtitle");
  }
</style>
